/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiShoppingCart, FiCheckCircle, FiXCircle, FiRotateCcw, FiSearch, FiDownload, FiFilter } from "react-icons/fi";
import PageContainer from "../../components/PageContainer";
import UnifiedCard from "../../components/UnifiedCard";
import ResponsiveTable from "../../components/ResponsiveTable";
import { useUI } from "../../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../../hooks/useTenantStorePermissions";
import NoAccess from "../../components/NoAccess";
import { formatNumber } from "utils/formatNumber";
import * as XLSX from "xlsx";
import clsx from "clsx";

export default function OrdersReports() {
    const { theme, lang, colors } = useUI();
    const isDark = theme === "dark";
    const isArabic = lang === "ar";
    const perms = useTenantStorePermissions();
    const [searchParams, setSearchParams] = useSearchParams();

    const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
    const tenantId = parseInt(localStorage.getItem("tenantId") || "0");

    const [activeStatus, setActiveStatus] = useState(searchParams.get("status") || "all");
    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [counts, setCounts] = useState({ all: 0, completed: 0, cancelled: 0, returned: 0 });
    const [searchTerm, setSearchTerm] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const normalizeStatusKey = (raw) => {
        if (!raw || typeof raw !== "string") return "new";
        const v = raw.toLowerCase().trim();
        if (["pending", "قيد المعالجة", "new", "جديد"].includes(v)) return "new";
        if (["قيد التجهيز", "تم التجهيز", "preparing", "prepared"].includes(v)) return "preparing";
        if (["ready", "جاهز"].includes(v)) return "ready";
        if (["جاري التوصيل", "delivering", "in delivery"].includes(v)) return "delivering";
        if (["واصل", "delivered", "completed", "مكتمل"].includes(v)) return "delivered";
        if (v.includes("راجع") || v.includes("returned")) return "returned";
        if (["ملغي", "cancelled", "canceled"].includes(v)) return "cancelled";
        return "new";
    };

    const statusCards = [
        {
            id: "all",
            key: "all",
            label_ar: "الكل",
            label_en: "All",
            icon: <FiShoppingCart />,
            color: "#7400BF", // Purple
        },
        {
            id: "completed",
            key: "delivered",
            label_ar: "الطلبات المكتملة",
            label_en: "Completed Orders",
            icon: <FiCheckCircle />,
            color: "#10b981", // Green
        },
        {
            id: "cancelled",
            key: "cancelled",
            label_ar: "الطلبات الملغية",
            label_en: "Cancelled Orders",
            icon: <FiXCircle />,
            color: "#ef4444", // Red
        },
        {
            id: "returned",
            key: "returned",
            label_ar: "الطلبات المسترجعة",
            label_en: "Returned Orders",
            icon: <FiRotateCcw />,
            color: "#f59e0b", // Orange
        },
    ];

    const fetchOrders = async () => {
        if (!perms.viewSalesReport || !tenantId) return;

        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const url = new URL(`${baseUrl}/GetTenantOrders`);
            url.searchParams.append("tenantId", tenantId.toString());
            url.searchParams.append("statusId", "-1"); // Always fetch all for counting
            url.searchParams.append("OrderNo", "-1");
            url.searchParams.append("CustomerId", "-1");

            if (dateFrom) url.searchParams.append("fromDate", dateFrom);
            if (dateTo) url.searchParams.append("toDate", dateTo);

            const res = await fetch(url.toString(), {
                headers: {
                    "Accept": "*/*",
                    "Authorization": token ? `Bearer ${token}` : "",
                },
            });

            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data)) {
                    let totalOrders = 0;
                    let comp = 0;
                    let canc = 0;
                    let ret = 0;

                    const mapped = data.map(order => {
                        const sKey = normalizeStatusKey(order.status);
                        totalOrders++;
                        if (sKey === "delivered") comp++;
                        else if (sKey === "cancelled") canc++;
                        else if (sKey === "returned") ret++;

                        let total = Number(order.netAmount || order.totalAmount || 0);
                        if (total === 0 && order.details && order.details.length > 0) {
                            total = order.details.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
                        }
                        return { ...order, displayAmount: total, statusKey: sKey };
                    });

                    setOrders(mapped);
                    setCounts({ all: totalOrders, completed: comp, cancelled: canc, returned: ret });
                } else {
                    setOrders([]);
                    setCounts({ all: 0, completed: 0, cancelled: 0, returned: 0 });
                }
            } else {
                console.error("Failed to fetch orders");
            }
        } catch (err) {
            console.error("Error fetching orders:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
        const statusFromUrl = searchParams.get("status");
        if (statusFromUrl && statusFromUrl !== activeStatus) {
            setActiveStatus(statusFromUrl);
        }
    }, [activeStatus, searchParams, tenantId]);

    const filteredOrders = useMemo(() => {
        let result = orders;

        // Filter by Card Status
        const currentCard = statusCards.find(c => c.id === activeStatus);
        if (currentCard && currentCard.key !== "all") {
            result = result.filter(o => o.statusKey === currentCard.key);
        }

        if (!searchTerm) return result;
        const q = searchTerm.toLowerCase();
        return result.filter(o =>
            o.orderNo?.toString().includes(q) ||
            o.customerName?.toLowerCase().includes(q) ||
            o.customerPhone1?.includes(q)
        );
    }, [orders, searchTerm, activeStatus]);

    const exportToExcel = () => {
        const dataToExport = filteredOrders.map(o => ({
            [isArabic ? "رقم الطلب" : "Order No"]: o.orderNo,
            [isArabic ? "اسم العميل" : "Customer Name"]: o.customerName,
            [isArabic ? "رقم الهاتف" : "Phone"]: o.customerPhone1,
            [isArabic ? "المدينة" : "City"]: o.city,
            [isArabic ? "التاريخ" : "Date"]: o.orderDate,
            [isArabic ? "المبلغ الصافي" : "Net Amount"]: o.displayAmount,
            [isArabic ? "طريقة الدفع" : "Payment Method"]: o.paymentMethodName,
            [isArabic ? "حالة الدفع" : "Payment Status"]: o.paymentStatus,
        }));

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Orders");
        XLSX.writeFile(wb, `${activeStatus}_orders_report.xlsx`);
    };

    const getStatusInfo = (key) => {
        const statusMap = {
            new: { label: isArabic ? "جديد" : "New", color: "#3B82F6" },
            preparing: { label: isArabic ? "جاري التجهيز" : "Preparing", color: "#6366F1" },
            ready: { label: isArabic ? "جاهز" : "Ready", color: "#8B5CF6" },
            delivering: { label: isArabic ? "جاري التوصيل" : "Delivering", color: "#F59E0B" },
            delivered: { label: isArabic ? "مكتمل" : "Completed", color: "#10B981" },
            returned: { label: isArabic ? "مسترجع" : "Returned", color: "#EF4444" },
            cancelled: { label: isArabic ? "ملغي" : "Cancelled", color: "#6B7280" },
            partially_returned: { label: isArabic ? "مسترجع جزئيًا" : "Partially Returned", color: "#EC4899" },
        };
        return statusMap[key] || { label: key, color: "#6B7280" };
    };

    if (!perms.viewSalesReport) return <NoAccess />;

    return (
        <PageContainer title={isArabic ? "تقارير الطلبات" : "Orders Reports"}>
            {/* Cards Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {statusCards.map((card) => (
                    <motion.div
                        key={card.id}
                        whileHover={{ y: -5 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                            setActiveStatus(card.id);
                            setSearchParams({ status: card.id });
                        }}
                        className="cursor-pointer group"
                    >
                        <UnifiedCard
                            className={clsx(
                                "transition-all duration-300",
                                activeStatus === card.id ? "ring-2 ring-offset-2" : ""
                            )}
                            style={{
                                borderColor: activeStatus === card.id ? card.color : "transparent",
                                backgroundColor: activeStatus === card.id
                                    ? (isDark ? "rgba(30, 41, 59, 0.5)" : "#fff")
                                    : (isDark ? "rgba(30, 41, 59, 0.3)" : "#fff"),
                                ringColor: card.color
                            }}
                        >
                            <div className="flex flex-col items-center justify-center py-6 text-center">
                                <div
                                    className="w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-lg transition-transform "
                                    style={{
                                        backgroundColor: activeStatus === card.id ? card.color : (isDark ? "#334155" : "#f1f5f9"),
                                        color: activeStatus === card.id ? "#fff" : card.color
                                    }}
                                >
                                    {React.cloneElement(card.icon, { size: 32 })}
                                </div>
                                <h3 className={clsx(
                                    "text-lg font-bold mb-1",
                                    activeStatus === card.id
                                        ? (isDark ? "text-white" : "text-gray-900")
                                        : "text-slate-500"
                                )}>
                                    {isArabic ? card.label_ar : card.label_en}
                                </h3>
                                <div className="text-2xl font-black mt-1" style={{ color: activeStatus === card.id ? (isDark ? "#fff" : card.color) : (isDark ? "#94a3b8" : "#64748b") }}>
                                    {counts[card.id]}
                                </div>
                            </div>
                        </UnifiedCard>
                    </motion.div>
                ))}
            </div>

            {/* Filters & Actions */}
            <UnifiedCard hoverable={false} className="mb-8">
                <div className="flex flex-wrap items-end gap-4 p-2">
                    <div className="flex-1 min-w-[200px]">
                        <label className="block mb-2 text-sm font-bold opacity-70">
                            {isArabic ? "البحث برقم الطلب أو العميل" : "Search by Order No or Customer"}
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={isArabic ? "ابحث هنا..." : "Search here..."}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-10 py-2.5 rounded-xl border focus:ring-2 outline-none transition-all"
                                style={{
                                    backgroundColor: isDark ? "#1e293b" : "#fff",
                                    borderColor: isDark ? "#334155" : "#e2e8f0",
                                    color: isDark ? "#fff" : "#000"
                                }}
                            />
                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40 ml-7" />
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <div className="w-40">
                            <label className="block mb-2 text-sm font-bold opacity-70">{isArabic ? "من تاريخ" : "From Date"}</label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="w-full px-4 py-2 rounded-xl border outline-none"
                                style={{ backgroundColor: isDark ? "#1e293b" : "#fff", borderColor: isDark ? "#334155" : "#e2e8f0" }}
                            />
                        </div>
                        <div className="w-40">
                            <label className="block mb-2 text-sm font-bold opacity-70">{isArabic ? "إلى تاريخ" : "To Date"}</label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className="w-full px-4 py-2 rounded-xl border outline-none"
                                style={{ backgroundColor: isDark ? "#1e293b" : "#fff", borderColor: isDark ? "#334155" : "#e2e8f0" }}
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 ml-auto">
                        <button
                            onClick={fetchOrders}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-white transition-all shadow-md text-sm"
                            style={{ backgroundColor: colors.mainColor }}
                        >
                            <FiSearch size={18} /> {isArabic ? "بحث" : "Search"}
                        </button>
                        <button
                            onClick={exportToExcel}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-white transition-all shadow-md text-sm"
                            style={{ backgroundColor: "#10b981" }}
                        >
                            <FiDownload size={18} /> {isArabic ? "تنزيل إكسل" : "Excel"}
                        </button>
                    </div>
                </div>
            </UnifiedCard>

            {/* Table Section */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={activeStatus + loading}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                >
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="w-12 h-12 border-4 border-t-transparent animate-spin rounded-full" style={{ borderColor: colors.mainColor, borderTopColor: 'transparent' }}></div>
                        </div>
                    ) : (
                        <ResponsiveTable
                            columns={[
                                { header: isArabic ? "رقم الطلب" : "Order No", sortKey: "orderNo" },
                                { header: isArabic ? "العميل" : "Customer", sortKey: "customerName" },
                                { header: isArabic ? "المدينة" : "City" },
                                { header: isArabic ? "التاريخ" : "Date", sortKey: "orderDate" },
                                { header: isArabic ? "المبلغ" : "Amount", sortKey: "netAmount", align: "center" },
                                { header: isArabic ? "طريقة الدفع" : "Payment", sortKey: "paymentMethodName" },
                                { header: isArabic ? "حالة الطلب" : "Order Status", align: "center" },
                                { header: isArabic ? "حالة الدفع" : "Status", align: "center" },
                            ]}
                            data={filteredOrders}
                            renderRow={(row) => {
                                const sInfo = getStatusInfo(row.statusKey);
                                return (
                                    <>
                                        <td className="px-6 py-4 font-bold">#{row.orderNo}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="font-medium">{row.customerName}</span>
                                                <span className="text-xs opacity-60">{row.customerPhone1}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">{row.city}</td>
                                        <td className="px-6 py-4 text-sm opacity-80">{row.orderDate}</td>
                                        <td className="px-6 py-4 text-center font-bold" style={{ color: colors.mainColor }}>
                                            {formatNumber(row.displayAmount)} {isArabic ? "د.ع" : "IQD"}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium">{row.paymentMethodName}</td>
                                        <td className="px-6 py-4 text-center">
                                            <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: `${sInfo.color}20`, color: sInfo.color }}>
                                                {sInfo.label}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={clsx(
                                                "px-3 py-1 rounded-full text-xs font-bold",
                                                row.isPaid ? "bg-emerald-500/10 text-emerald-500" : "bg-rose-500/10 text-rose-500"
                                            )}>
                                                {isArabic ? (row.isPaid ? "مدفوع" : "غير مدفوع") : row.paymentStatus}
                                            </span>
                                        </td>
                                    </>
                                );
                            }}
                        />
                    )}
                </motion.div>
            </AnimatePresence>
        </PageContainer>
    );
}
